import React from 'react';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import SwitchInput from '../../forms/SwitchInput';
import { useTranslation } from 'react-i18next';
import NotificationSubscription from '../../../models/general/notifications/notification-trigger';
import {
  NotificationGroupType,
  NotificationRepeat,
  NotificationSendMethod,
  NotificationTrigger,
} from '../../../models/enums/notifications';
import { useAppDispatch, useAppSelector } from '../../../store';
import {
  createNotificationSubscription,
  patchNotificationSubscriptionById,
} from '../../../store/general/general-actions';
import { selectUser } from '../../../store/general/general-slice-selectors';
import { EventRepeatIcon } from '@common/utils/icons/icons';
import TooltipIconButton from '@common/components/Inputs/TooltipIconButton';
import RepeatOptionPopup from './RepeatOptionPopup';

const SubscriptionSwitch: React.FC<{
  trigger: NotificationTrigger;
  repeat?: NotificationRepeat | null;
  sendMethod: NotificationSendMethod;
  notification?: NotificationSubscription;
  isChecked: boolean;
  groupType: NotificationGroupType;
}> = ({ notification, repeat, trigger, isChecked, sendMethod }) => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const currentUser = useAppSelector((state) => selectUser(state));

  const [repeatAnchorEl, setRepeatAnchorEl] = React.useState<HTMLButtonElement | null>(null);

  const toggleNotificationActiveClickHandler = () => {
    if (notification?.id != null) {
      dispatch(
        patchNotificationSubscriptionById({
          id: notification.id,
          data: {
            pushActive: sendMethod === NotificationSendMethod.PUSH ? !isChecked : undefined,
            emailActive: sendMethod === NotificationSendMethod.EMAIL ? !isChecked : undefined,
          },
        }),
      );
    } else {
      dispatch(
        createNotificationSubscription({
          pushActive: sendMethod === NotificationSendMethod.PUSH ? true : false,
          emailActive: sendMethod === NotificationSendMethod.EMAIL ? true : false,
          repeat,
          trigger,
          userId: currentUser.data.id,
        }),
      );
    }
  };

  const openRepeatPopoverClickHandler = (event: React.MouseEvent<HTMLButtonElement>) => {
    setRepeatAnchorEl(event.currentTarget);
  };

  const closeRepeatPopoverHandler = () => {
    setRepeatAnchorEl(null);
  };

  const patchRepeatOptionHandler = (repeat: NotificationRepeat) => {
    if (notification?.id != null) {
      dispatch(
        patchNotificationSubscriptionById({
          id: notification.id,
          data: {
            repeat,
          },
        }),
      );
    } else {
      dispatch(
        createNotificationSubscription({
          pushActive: sendMethod === NotificationSendMethod.PUSH ? true : false,
          emailActive: sendMethod === NotificationSendMethod.EMAIL ? true : false,
          repeat,
          trigger,
          userId: currentUser.data.id,
        }),
      );
    }
    closeRepeatPopoverHandler();
  };

  return (
    <React.Fragment>
      <Stack direction="row" justifyContent="space-between" alignItems="center" width="100%">
        <SwitchInput
          label={
            <Stack display="inline-flex" spacing={0.5}>
              <Typography>{t('notification-trigger-' + trigger)}</Typography>
              {repeat != null && (
                <Typography variant="caption">{t('notification-repeat-' + repeat)}</Typography>
              )}
              <Typography variant="caption">
                {t('notification-trigger-default-description-' + trigger)}
              </Typography>
            </Stack>
          }
          isChecked={isChecked}
          onChange={toggleNotificationActiveClickHandler}
        />
        {repeat != null && (
          <TooltipIconButton
            tooltip={t('info-tooltip-repeat-option')}
            icon={<EventRepeatIcon />}
            onClick={openRepeatPopoverClickHandler}
          />
        )}
      </Stack>
      <RepeatOptionPopup
        currentRepeat={repeat ?? null}
        anchorEl={repeatAnchorEl}
        onOptionSelect={patchRepeatOptionHandler}
        onClose={closeRepeatPopoverHandler}
      />
    </React.Fragment>
  );
};

export default SubscriptionSwitch;
